<template>
	<div @keydown.esc="closeAllModals" class="group-settings-options">
		<FailBanner v-if="showFail" @close="showFail = false" marginright="50px">
			<template #alerttext>{{ alertText }}</template>
		</FailBanner>

		<div
			class="super-group-wrapper"
			v-if="chat.is_supergroup"
			@click="showSuperGroupModal = true"
		>
			<RocketLaunchIcon halo type="regular" class="super-group-icon" />
			<div class="super-group-content">
				<div class="super-group-title" v-text="$t('chat_super_title')" />
				<div class="super-group-desc">
					{{ $t('groupsettings_super_group_description') }}
					<div class="super-group-link" v-text="$t('common_learn_more')" />
				</div>
			</div>
		</div>

		<div class="group-name-wrapper" v-if="!chat.isdm">
			<div class="group-header">
				<GroupIconPicker :chatId="chat.id" @change="onIconChange" />
				<Button
					primary
					v-if="hasTitleChanges"
					:disabled="!canSaveTitleChanges"
					class="unified-save-button"
					@click="updateGroup"
					:loading="isSaving"
				>
					{{ $t('chat_messageaction_save') }}
				</Button>
			</div>

			<p class="group-settings-sub-heading" style="margin-top: 5px">
				<span>{{
					$t('groupsettings_name_settings_groupname').toLowerCase()
				}}</span>
			</p>
			<div class="group-settings-group-name">
				<input
					id="group-name"
					maxlength="100"
					class="group-settings-name"
					:class="{
						'validation-error': hasTitleChanges && title.trim() === '',
					}"
					:disabled="!canChangeTitle"
					type="text"
					name="txtGroupName"
					v-model="title"
				/>
				<div
					v-if="hasTitleChanges && title.trim() === ''"
					class="validation-error"
				>
					{{ $t('groupsettings_name_required') }}
				</div>
			</div>

			<p class="group-settings-sub-heading" style="margin-top: 20px">
				<span>{{
					$t('groupsettings_name_settings_nickname').toLowerCase()
				}}</span>
			</p>
			<div class="group-settings-group-nickname" style="position: relative">
				<input
					:placeholder="$t('groupsettings_name_settings_nickname_placeholder')"
					maxlength="100"
					class="group-settings-nickname nickname"
					type="text"
					name="txtNickname"
					v-model="groupNickname"
					data-cy="nickname"
				/>
			</div>
			<div class="nickname-info">
				{{ $t('groupsettings_name_settings_nickname_description') }}
			</div>
		</div>

		<div class="group-category-wrapper" v-if="!chat.isdm">
			<div class="category-header">
				<p class="group-settings-sub-heading">
					<span>{{
						$t('groupsettings_option_category_title').toLowerCase()
					}}</span>
				</p>
				<Button
					primary
					v-if="hasCategoryChanges"
					:disabled="!canSaveCategoryChanges"
					class="unified-save-button"
					@click="updateGroup"
					:loading="isSaving"
				>
					{{ $t('chat_messageaction_save') }}
				</Button>
			</div>

			<div
				class="group-settings-category group-settings-select-class select-no-clear"
			>
				<Dropdown
					v-model="category_id"
					:options="categoryOptions"
					:disabled="!canChangeCategory"
					class="group-category-select"
					search
				>
					<template #actionitem>
						<div @click="category_id = null">
							<Uncategorized />
						</div>
					</template>

					<template #selected="{ option }">
						<span
							class="category-selected"
							v-if="option"
							v-text="option.title"
						/>
						<Uncategorized v-if="!option && !category_id" />
					</template>

					<template v-slot="{ option }">
						{{ option.title }}
					</template>
				</Dropdown>
			</div>

			<p class="group-settings-sub-heading" style="margin-top: 20px">
				<span>{{
					$t('groupsettings_name_settings_nickname').toLowerCase()
				}}</span>
			</p>
			<div class="group-settings-category-nickname" style="position: relative">
				<input
					:placeholder="$t('groupsettings_name_settings_nickname_placeholder')"
					maxlength="100"
					class="group-settings-nickname nickname"
					type="text"
					name="txtNickname"
					v-model="categoryNickname"
					data-cy="categoryNickname"
				/>
			</div>
			<div class="nickname-info">
				{{ $t('groupsettings_name_settings_nickname_description') }}
			</div>
		</div>

		<div class="change-controls" v-if="groupOwner && !chat.isdm">
			<GroupPermissions :chat="chat" />
		</div>

		<ButtonDescription
			data-cy="hideGroupBtn"
			:buttonText="$t('groupsettings_hide_confirm_button')"
			@buttonClick="determineHide()"
		>
			{{ hideBtnExplanation }}
		</ButtonDescription>

		<ButtonDescription
			data-cy="leaveGroupBtn"
			v-if="!chat.isdm && canLeaveGroup"
			:buttonText="$t('groupsettings_option_leavebubble')"
			@buttonClick="showLeaveModal = true"
		>
			{{ $t('groupsettings_option_leavebubble_desc') }}
		</ButtonDescription>

		<ButtonDescription
			data-cy="deleteGroupBtn"
			v-if="!chat.isdm && canDeleteChat"
			@buttonClick="showDeleteModal = true"
			color="red"
			:buttonText="$t('groupsettings_option_deletebubble')"
		>
			{{ $t('groupsettings_option_deletebubble_desc') }}
		</ButtonDescription>

		<LeaveModal
			v-if="showLeaveModal"
			@close="showLeaveModal = false"
			:chatId="bubble_id"
			data-cy="leaveModal"
		/>

		<HideModal
			v-if="showHideModal"
			@close="showHideModal = false"
			@hide="hideGroup()"
			:buttonText="'Hide Group'"
			:showConfirm="true"
			:showSpinner="showHideSpinner"
			data-cy="hideModal"
		>
			<template #titletext>{{ $t('chat_drawer_section_hide_group') }}</template>
			<template #subtext>{{
				$t('chat_drawer_section_hide_description_group')
			}}</template>
		</HideModal>

		<DeleteModal
			v-if="showDeleteModal"
			@close="showDeleteModal = false"
			@delete="deleteGroup"
			:title="$t('groupsettings_option_deletebubble')"
			:buttonText="'Delete Group'"
			:showConfirm="true"
			:isProcessing="showDeleteSpinner"
			:bodyText="$t('groupsettings_delete_confirm_message')"
			data-cy="deleteModal"
		/>

		<SuperGroupModal
			v-if="showSuperGroupModal"
			:chatId="chat.id"
			@close="showSuperGroupModal = false"
		/>
	</div>
</template>

<script>
import store, {
	getMyMembership,
	setMembership,
	getChat,
	getCategory,
	getCategories,
	getMyMembershipForBubble,
} from '@/store'
import { post } from '@/helpers/api'
import { updateChat } from '@/api/chat'
import GroupIconPicker from '@/components/common/GroupIconPicker'
import { ButtonDescription, Dropdown, Button } from '@/components/common'
import FailBanner from '@/components/FailBanner'
import DeleteModal from '@/components/DeleteModal'
import HideModal from '@/components/HideModal'
import LeaveModal from '@/components/Home/LeaveModal'
import i18n from '@/i18n'
import { redirectToLastActiveGroup, getErrorText } from '@/helperMethods'
import GroupPermissions from './GroupPermissions'
import SuperGroupModal from '@/components/Home/SuperGroupModal'
import { RocketLaunchIcon } from '@/icons'
import { updateMembership } from '@/api/membership'
import Uncategorized from '@/components/OrgManagement/common/Uncategorized'
import {
	chatCan,
	CHANGE_TITLE,
	CHANGE_CATEGORY,
	LEAVE_GROUP,
	DELETE_GROUP,
} from '@/helpers/permissions'

export default {
	components: {
		Dropdown,
		FailBanner,
		DeleteModal,
		LeaveModal,
		HideModal,
		ButtonDescription,
		GroupPermissions,
		RocketLaunchIcon,
		SuperGroupModal,
		GroupIconPicker,
		Button,
		Uncategorized,
	},
	props: ['bubble_id'],
	data() {
		return {
			store,
			chat: {},
			title: '',
			categoryNickname: '',
			originalCategoryNickname: '',
			groupNickname: '',
			originalGroupNickname: '',
			categoryNicknameIndicator: false,
			groupNicknameIndicator: false,
			originalTitle: '',
			category_id: null,
			showDeleteModal: false,
			showLeaveModal: false,
			categoryOptions: [],
			showFail: false,
			alertText: '',
			showDeleteSpinner: false,
			showHideSpinner: false,
			showHideModal: false,
			showSuperGroupModal: false,
			originalCategoryId: null,
			iconIndicator: false,
			isSaving: false,
			iconData: null,
		}
	},

	watch: {
		categoryNickname: function (val) {
			if (val === this.originalCategoryNickname) {
				this.categoryNicknameIndicator = false
			} else {
				this.categoryNicknameIndicator = true
			}
		},

		groupNickname: function (val) {
			if (val === this.originalGroupNickname) {
				this.groupNicknameIndicator = false
			} else {
				this.groupNicknameIndicator = true
			}
		},

		category_id: function (newCategoryId) {
			// Get the nickname for the new category
			const newCategoryNickname = newCategoryId
				? getCategory(newCategoryId)?.usercategory?.alias ?? ''
				: '' // If uncategorized, clear the nickname

			this.categoryNickname = newCategoryNickname
			this.originalCategoryNickname = newCategoryNickname

			// Reset the indicator since we're now showing the correct nickname for this category
			this.categoryNicknameIndicator = false
		},

		chat(chat) {
			this.title = chat.title
			this.originalTitle = this.title
		},
	},

	created() {
		this.chat = getChat(this.bubble_id)
		this.title = this.chat.title
		this.originalTitle = this.title
		this.category_id = this.chat.category_id
	},

	mounted() {
		this.getCategories()

		this.categoryNickname =
			getCategory(this.category_id)?.usercategory?.alias ?? ''
		this.originalCategoryNickname = this.categoryNickname
		this.originalCategoryId = this.category_id

		this.groupNickname = getMyMembershipForBubble(this.bubble_id)?.alias ?? ''
		this.originalGroupNickname = this.groupNickname
	},

	computed: {
		groupIsHidden() {
			const membership = getMyMembership()
			return membership?.ishidden
		},

		groupOwner() {
			return getMyMembership()?.role === 'owner'
		},

		canChangeTitle() {
			return chatCan(this.chat, CHANGE_TITLE)
		},

		canChangeCategory() {
			return chatCan(this.chat, CHANGE_CATEGORY)
		},

		canLeaveGroup() {
			return chatCan(this.chat, LEAVE_GROUP)
		},

		canDeleteChat() {
			return chatCan(this.chat, DELETE_GROUP)
		},

		hideBtnExplanation() {
			if (this.chat.is_dm && this.chat.dm_memberships_count > 2) {
				return this.$t('groupsettings_hidemultidm_explanation')
			} else if (this.chat.is_dm) {
				return this.$t('groupsettings_hidedm_explanation')
			} else {
				return this.$t('groupsettings_hidegroup_explanation')
			}
		},

		hasCategoryChanges() {
			return (
				this.categoryNicknameIndicator ||
				this.category_id !== this.originalCategoryId
			)
		},

		hasTitleChanges() {
			return (
				this.title !== this.originalTitle ||
				(this.groupNicknameIndicator &&
					this.groupNickname !== this.originalGroupNickname) ||
				this.iconIndicator
			)
		},

		canSaveTitleChanges() {
			return this.hasTitleChanges && this.title.trim() !== ''
		},

		canSaveCategoryChanges() {
			return this.hasCategoryChanges
		},
	},

	methods: {
		async onIconChange(data) {
			this.iconIndicator = true
			this.iconData = data
		},

		async updateGroup() {
			const updates = {}
			const membershipUpdates = {}
			const categoryUpdates = {}

			// Simplified: Check each property directly against original values
			if (this.title !== this.originalTitle) {
				updates.title = this.title
			}

			if (this.iconIndicator && this.iconData) {
				updates.icon_emoji = null
				updates.icon_image_key = null
				Object.assign(updates, this.iconData)
			}

			if (this.category_id !== this.originalCategoryId) {
				updates.category_id = this.category_id
			}

			if (this.groupNickname !== this.originalGroupNickname) {
				membershipUpdates.bubble_id = this.bubble_id
				membershipUpdates.alias = this.groupNickname
			}

			if (this.categoryNickname !== this.originalCategoryNickname) {
				categoryUpdates.category_id = this.category_id
				categoryUpdates.alias = this.categoryNickname
			}

			try {
				this.isSaving = true

				// Only make API calls for properties that changed
				const promises = []

				if (Object.keys(updates).length > 0) {
					promises.push(updateChat(this.chat.id, updates, true))
				}

				if (Object.keys(membershipUpdates).length > 0) {
					promises.push(updateMembership(membershipUpdates))
				}

				if (Object.keys(categoryUpdates).length > 0) {
					promises.push(post('/api/v1/category.update', categoryUpdates))
				}

				await Promise.all(promises)

				// Reset indicators and update original values
				this.iconIndicator = false
				this.iconData = null
				this.groupNicknameIndicator = false
				this.categoryNicknameIndicator = false

				// Update original values to current values
				this.originalTitle = this.title
				this.originalGroupNickname = this.groupNickname
				this.originalCategoryNickname = this.categoryNickname
				this.originalCategoryId = this.category_id

				this.$alert.success(
					this.$t('admin_people_changes_saved_confirmation_label')
				)
			} catch (e) {
				console.error(e)
				this.$alert.error(e)
			} finally {
				this.isSaving = false
			}
		},

		determineHide() {
			if (this.groupIsHidden) {
				this.unhideGroup()
			} else {
				this.showHideModal = true
			}
		},

		unhideGroup() {
			const membership = getMyMembership()
			membership.ishidden = false
			setMembership(membership)

			let post_data = {
				bubble_id: this.bubble_id,
				ishidden: 0,
			}

			post('/api/v1/membership.update', post_data)
				.then(() => {})
				.catch((e) => {
					membership.ishidden = true
					setMembership(membership)
				})
		},

		getCategories() {
			this.categoryOptions = getCategories().map((item) => {
				return { id: item.id, title: item.title }
			})
		},

		deleteGroup() {
			this.showDeleteSpinner = true
			post('/api/v1/bubble.delete', { bubble_id: this.bubble_id })
				.then(() => {
					this.showDeleteModal = false
					redirectToLastActiveGroup()
				})
				.catch((error) => {
					console.error(error)
					this.showDeleteSpinner = false
					this.$alert.error()
				})
		},

		hideGroup() {
			// optimistically update our membership to be marked as hidden.
			const membership = getMyMembership()
			membership.ishidden = true
			setMembership(membership)

			this.showHideSpinner = true

			let post_data = {
				bubble_id: this.bubble_id,
				ishidden: 1,
			}

			post('/api/v1/membership.update', post_data)
				.then(({ data }) => {
					if (!data.ok) {
						const key = getErrorText(
							data.error,
							'chat_drawer_section_error_hide'
						)
						throw new Error(i18n.global.t(key, { GroupName: this.chat.title }))
					}
					this.showHideModal = false
					setMembership(data.membership)
					redirectToLastActiveGroup()
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(error)

					membership.ishidden = false
					setMembership(membership)
				})
		},

		closeAllModals() {
			this.showDeleteModal = false
			this.showLeaveModal = false
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.group-settings-sub-heading {
	margin-top: 0;
	margin-bottom: 5px;
}

.group-settings-sub-heading span {
	text-transform: capitalize;
}

.group-settings-name,
.group-settings-nickname {
	height: 32px;
	width: 100%;
	border-radius: 4px;
	border: solid 1px #dfe1e3;
	padding: 16px 112px 16px 10px;
	font-family: Avenir-Medium;
	font-size: 16px;
	color: #2c2d30;
}

#group-name,
.nickname {
	outline: none;
}

.change-controls {
	position: relative;
}

.group-header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 20px;
	gap: 16px;
}

.validation-error {
	font-family: Avenir-Heavy;
	font-size: 14px;
	color: var(--red-200);
}

.group-settings-input-spinner {
	position: absolute;
}

#verySmallSpinner {
	position: relative;
	width: 22px;
	height: 22px;
	display: inline-block;
	padding: 10px;
	border-radius: 10px;
	background: #ffffff;
	vertical-align: top;
	margin-top: 4.1px;
}

.ios-spinner div {
	width: 6%;
	height: 16%;
	background: #9c9c9d;
	position: absolute;
	left: 49%;
	top: 43%;
	opacity: 0;
	-webkit-border-radius: 50px;
	-webkit-animation: fade 1s linear infinite;
}

@-webkit-keyframes fade {
	from {
		opacity: 1;
	}
	to {
		opacity: 0.25;
	}
}

.ios-spinner div.bar1 {
	-webkit-transform: rotate(0deg) translate(0, -130%);
	-webkit-animation-delay: 0s;
}

.ios-spinner div.bar2 {
	-webkit-transform: rotate(30deg) translate(0, -130%);
	-webkit-animation-delay: -0.9167s;
}

.ios-spinner div.bar3 {
	-webkit-transform: rotate(60deg) translate(0, -130%);
	-webkit-animation-delay: -0.833s;
}
.ios-spinner div.bar4 {
	-webkit-transform: rotate(90deg) translate(0, -130%);
	-webkit-animation-delay: -0.7497s;
}
.ios-spinner div.bar5 {
	-webkit-transform: rotate(120deg) translate(0, -130%);
	-webkit-animation-delay: -0.667s;
}
.ios-spinner div.bar6 {
	-webkit-transform: rotate(150deg) translate(0, -130%);
	-webkit-animation-delay: -0.5837s;
}
.ios-spinner div.bar7 {
	-webkit-transform: rotate(180deg) translate(0, -130%);
	-webkit-animation-delay: -0.5s;
}
.ios-spinner div.bar8 {
	-webkit-transform: rotate(210deg) translate(0, -130%);
	-webkit-animation-delay: -0.4167s;
}
.ios-spinner div.bar9 {
	-webkit-transform: rotate(240deg) translate(0, -130%);
	-webkit-animation-delay: -0.333s;
}
.ios-spinner div.bar10 {
	-webkit-transform: rotate(270deg) translate(0, -130%);
	-webkit-animation-delay: -0.2497s;
}
.ios-spinner div.bar11 {
	-webkit-transform: rotate(300deg) translate(0, -130%);
	-webkit-animation-delay: -0.167s;
}
.ios-spinner div.bar12 {
	-webkit-transform: rotate(330deg) translate(0, -130%);
	-webkit-animation-delay: -0.0833s;
}

.group-settings-options {
	@include flex-column(24px);
}

.super-group-wrapper,
.group-name-wrapper,
.group-category-wrapper {
	border: 1px solid var(--neutral-200);
	border-radius: 4px;
	padding: 16px 24px 16px 16px;
	background: var(--neutral-100);
}
.super-group-wrapper {
	cursor: pointer;
	padding: 16px;
	@include flex(12px);
	.super-group-content {
		@include flex-column(4px);
		.super-group-title {
			@include fontFamily(900);
		}
		.super-group-desc {
			@include flex(8px);
			color: var(--neutral-600);
			.super-group-link {
				color: var(--teal-300);
			}
		}
	}
	> .pronto-icon {
		color: var(--teal-300);
		font-size: 18px;
		&::before {
			width: 2em;
			height: 2em;
			background-color: white;
			opacity: 1;
		}
	}
}

.nickname-info {
	font-family: Avenir-Medium;
	font-size: 14px;
	color: #8e8e8e;
	margin-top: 10px;
}

.group-icon-picker {
	margin-bottom: 24px;
	:deep(.icon-picker-container) {
		background-color: var(--neutral-0);
	}
}

.category-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 16px;
	gap: 16px;
	min-height: 40px;

	.group-settings-sub-heading {
		margin: 0;
	}
}

.dropdown-container {
	.uncategorized {
		color: var(--neutral-800);
	}
}

.group-category-select {
	:deep(.dropdown-options-container.open .dropdown-search) {
		display: none !important;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.group-settings-name,
	.group-settings-nickname {
		background: var(--neutral-100) !important;
		border: solid 1px var(--neutral-100) !important;
		color: var(--neutral-800) !important;
	}
	.super-group-wrapper,
	.group-name-wrapper,
	.group-category-wrapper {
		background: var(--neutral-0);
	}
}
</style>
